import { useRef, useState } from "react";
import { Paperclip, Mic, Square, ArrowUp, X } from "lucide-react";
import { toast } from "sonner";
import { KindIcon } from "@/components/Badges";
import { t } from "@/i18n";
import { AudioLines } from "lucide-react";
import { useApi } from "@/lib/api";
import { DictateButton } from "@/components/DictateButton";

type Props = { busy: boolean; recordLabel: string; onAsk: (t: string) => void; onRecord: (files: File[], note: string) => Promise<boolean> };

const kindOf = (f: File) => (f.type.startsWith("image/") ? "photo" : f.type.startsWith("video/") ? "video" : f.type.startsWith("audio/") ? "audio" : "document");

function useRecorder(onDone: (f: File) => void) {
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        onDone(new File([new Blob(chunks.current, { type: "audio/webm" })], `voice-note-${Date.now()}.webm`, { type: "audio/webm" }));
      };
      mr.start();
      setRec(mr);
    } catch {
      toast.error(t("Microphone not available"));
    }
  };
  const stop = () => { rec?.stop(); setRec(null); };
  return { recording: !!rec, start, stop };
}

export function Composer({ busy, recordLabel, onAsk, onRecord }: Props) {
  const api = useApi();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const rec = useRecorder((f) => setFiles((p) => [...p, f]));
  const recordMode = files.length > 0;

  const submit = async () => {
    if (busy) return;
    if (recordMode) {
      if (await onRecord(files, text)) { setFiles([]); setText(""); }
    } else if (text.trim()) {
      onAsk(text.trim());
      setText("");
    }
  };

  return (
    <div className="stack-sm" data-testid="composer">
      {recordMode && (
        <div className="flex flex-wrap items-center gap-2 px-2" data-testid="composer-attachments">
          {files.map((f, i) => (
            <span key={i} className="chip chip-muted !py-1.5" data-testid={`composer-attachment-${i}`}>
              <KindIcon kind={kindOf(f)} size={13} /> <span className="max-w-[9rem] truncate">{f.name}</span>
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={t("Remove")}><X size={12} /></button>
            </span>
          ))}
          <span className="text-xs faint">{t("Add a note, then tap {label}.", { label: recordLabel })}</span>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex items-center gap-1 rounded-full border border-[var(--border-color)] bg-[var(--bg-surface)] pl-2 pr-1.5 py-1.5 shadow-[0_8px_30px_-12px_rgba(40,30,10,0.25)]">
        <button type="button" onClick={() => input.current?.click()} className="w-10 h-10 rounded-full grid place-items-center faint hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors" title={t("Attach")} data-testid="composer-attach-button"><Paperclip size={19} /></button>
        <button type="button" onClick={rec.recording ? rec.stop : rec.start} className={`w-10 h-10 rounded-full grid place-items-center transition-colors ${rec.recording ? "bg-[var(--status-error)] text-white" : "faint hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]"}`} title={t(rec.recording ? "Stop" : "Record")} data-testid="composer-record-button">
          {rec.recording ? <Square size={15} /> : <Mic size={19} />}
        </button>
        <input ref={input} type="file" multiple hidden accept="video/*,audio/*,image/*,.pdf,.txt,.md,.csv" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="composer-file-input" />
        {!recordMode && <DictateButton post={(fd: FormData) => api.post("/transcribe", fd)} onText={(s: string) => setText((p) => (p ? `${p} ${s}` : s))} icon={AudioLines} testid="composer-dictate-button" />}
        <input className="flex-1 min-w-0 bg-transparent outline-none text-[1rem] px-2 placeholder:text-[var(--text-muted)]" value={text} onChange={(e) => setText(e.target.value)} placeholder={t(rec.recording ? "Recording…" : recordMode ? "Add a note (optional)" : "Message")} data-testid="ask-input" />
        {recordMode ? (
          <button className="btn btn-primary btn-sm !bg-[var(--status-ok)]" disabled={busy || rec.recording} data-testid="composer-record-submit-button">{recordLabel}</button>
        ) : (
          <button className="w-10 h-10 rounded-full grid place-items-center bg-[var(--accent-primary)] text-[var(--accent-ink)] disabled:opacity-30 transition-opacity" disabled={busy || !text.trim()} data-testid="ask-submit-button"><ArrowUp size={19} /></button>
        )}
      </form>
    </div>
  );
}
